import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { JWTPayload } from "@its-engineering-and-development/jwt-auth-client";
import type { RootState } from "./index";

// ============================================================================
// HELPERS
// ============================================================================

const ACCESS_TOKEN_KEY = "itseng_access_token";
const LEGACY_REFRESH_TOKEN_KEY = "itseng_refresh_token";

function persistAccessToken(accessToken: string): void {
    try {
        localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
    } catch {
        console.warn("Failed to persist access token");
    }
}

function clearStoredTokens(): void {
    try {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
    } catch {
        console.warn("Failed to clear tokens from localStorage");
    }
}

// ============================================================================
// STATE
// ============================================================================

export interface AuthState {
    /** Persisted to localStorage */
    accessToken: string | null;
    /** Derived from accessToken — use authClient.decodeToken() in components */
    user: JWTPayload | null;
    isAuthenticated: boolean;
    /** False until AuthGuard has completed the access-token check */
    isResolved: boolean;
    /** True when the API denied an action (403) — triggers the sign-out notice. */
    accessDenied: boolean;
}

function buildInitialState(): AuthState {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    return {
        accessToken,
        user: null, // derived in components via authClient.decodeToken()
        isAuthenticated: !!accessToken,
        isResolved: false, // AuthGuard sets this true when check is complete
        accessDenied: false,
    };
}

// ============================================================================
// SLICE
// ============================================================================

export const authSlice = createSlice({
    name: "auth",
    initialState: buildInitialState,
    reducers: {
        /** Called after a successful auth-package login or OTC exchange. */
        setCredentials(state, action: PayloadAction<{ accessToken: string }>) {
            const { accessToken } = action.payload;

            state.accessToken = accessToken;
            state.isAuthenticated = true;
            state.isResolved = true;
            state.accessDenied = false;

            persistAccessToken(accessToken);
        },

        /** Called by AuthGuard when the current route is public or access token is valid. */
        markResolved(state) {
            state.isResolved = true;
        },

        /** Flag a 403 from the API so the app shows the access-denied notice + countdown. */
        markAccessDenied(state) {
            state.accessDenied = true;
        },

        /** Clear all auth state (logout). */
        clearCredentials(state) {
            state.accessToken = null;
            state.user = null;
            state.isAuthenticated = false;
            state.isResolved = true;
            state.accessDenied = false;

            clearStoredTokens();
        },
    },
});

export const { setCredentials, markResolved, markAccessDenied, clearCredentials } = authSlice.actions;

// ============================================================================
// SELECTORS
// ============================================================================

export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectAccessToken = (state: RootState) => state.auth.accessToken;
export const selectIsResolved = (state: RootState) => state.auth.isResolved;
export const selectAccessDenied = (state: RootState) => state.auth.accessDenied;
